import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Grid,
  Paper,
  Alert,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from "@mui/material";
import {
  Sports as SportsIcon,
  EmojiEvents as TrophyIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { LeagueDay, Match, Player, GameScore, Division, Round } from "../types";
import { apiRequest } from "../config/api";
import { useDivision } from "../contexts/DivisionContext";
import DivisionSelector from "../components/DivisionSelector";
import SwapAuditTrailDisplay from "../components/SwapAuditTrailDisplay";
import VenueBadge from "../components/VenueBadge";
import PlayerPairingsOverview from "../components/PlayerPairingsOverview";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function Matches() {
  const { leagueDayId } = useParams<{ leagueDayId: string }>();
  const navigate = useNavigate();
  const { currentDivision } = useDivision();

  const [leagueDay, setLeagueDay] = useState<LeagueDay | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]); // All players across divisions for substitution
  const [selectedRound, setSelectedRound] = useState(0);
  const [scoreDialogOpen, setScoreDialogOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const [pair1Score, setPair1Score] = useState(0);
  const [pair2Score, setPair2Score] = useState(0);
  const [forceComplete, setForceComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Substitution dialog state
  const [substitutionDialogOpen, setSubstitutionDialogOpen] = useState(false);
  const [originalPlayerId, setOriginalPlayerId] = useState("");
  const [substitutePlayerId, setSubstitutePlayerId] = useState("");
  const [substitutionError, setSubstitutionError] = useState("");
  const [applyPenalty, setApplyPenalty] = useState(false);
  const [penaltyPoints, setPenaltyPoints] = useState<number>(3);
  const [penaltyReason, setPenaltyReason] = useState("");
  const [retroactiveWarningOpen, setRetroactiveWarningOpen] = useState(false);

  // Scorer names state
  const [scorerNames, setScorerNames] = useState<{ [key: string]: string }>({});
  const [selectedRoundsForSubstitution, setSelectedRoundsForSubstitution] =
    useState<number[]>([]);

  // Edit pairs dialog state
  const [editPairsDialogOpen, setEditPairsDialogOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState<Match | null>(null);
  const [editPair1Player1, setEditPair1Player1] = useState("");
  const [editPair1Player2, setEditPair1Player2] = useState("");
  const [editPair2Player1, setEditPair2Player1] = useState("");
  const [editPair2Player2, setEditPair2Player2] = useState("");

  // On-demand swap analysis (for league days that didn't save an audit trail)
  const [liveSwapAuditTrail, setLiveSwapAuditTrail] = useState<any>(null);
  const [loadingSwapAnalysis, setLoadingSwapAnalysis] = useState(false);

  useEffect(() => {
    console.log("[Matches] Component mounted with leagueDayId:", leagueDayId);
    if (leagueDayId) {
      fetchLeagueDay();
      fetchPlayers();
    } else {
      console.error("[Matches] No leagueDayId provided in URL parameters");
      setError("No league day ID provided");
      setLoading(false);
    }
  }, [leagueDayId]);

  const fetchLeagueDay = async () => {
    try {
      console.log("[Matches] Fetching league day with ID:", leagueDayId);
      const response = await apiRequest(`/api/league-days/${leagueDayId}`);
      console.log("[Matches] League day response status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("[Matches] League day data received:", data);
        setLeagueDay(data);
        // Set initial tab to current round
        setSelectedRound((data.currentRound || 1) - 1);

        // Fetch scorer names for all matches
        const allMatches =
          data.rounds?.flatMap((round: Round) => round.matches) || [];
        fetchScorerNames(allMatches);
      } else {
        const errorText = await response.text();
        console.error(
          "[Matches] Failed to fetch league day:",
          response.status,
          errorText
        );
        setError(`League day not found (${response.status})`);
      }
    } catch (error) {
      console.error("Error fetching league day:", error);
      setError("Failed to fetch league day");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await apiRequest("/api/players");
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
        setAllPlayers(data); // Store all players for substitution
      }
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  };

  const handleAnalyseSwaps = async () => {
    if (!leagueDay?.playerAssignments || !leagueDay.division || !leagueDay.courtCount) return;
    setLoadingSwapAnalysis(true);
    try {
      const response = await apiRequest('/api/league-days/preview-swap', {
        method: 'POST',
        body: JSON.stringify({
          division: leagueDay.division,
          assignments: leagueDay.playerAssignments,
          courtCount: leagueDay.courtCount,
          seasonId: leagueDay.seasonId,
        }),
      });
      if (response.ok) {
        const trail = await response.json();
        setLiveSwapAuditTrail(trail);
      }
    } catch (err) {
      console.error('Error running swap analysis:', err);
    } finally {
      setLoadingSwapAnalysis(false);
    }
  };

  const [applyingSwap, setApplyingSwap] = useState(false);

  const handleApplySwap = async (auditTrail: any) => {
    if (!leagueDay || !auditTrail?.finalAssignments) return;
    if (!window.confirm('This will rewrite all match pairings to match the swap report. Continue?')) return;
    setApplyingSwap(true);
    try {
      const response = await apiRequest(`/api/league-days/${leagueDay.id}/apply-swap`, {
        method: 'POST',
        body: JSON.stringify({
          finalAssignments: auditTrail.finalAssignments,
          swapAuditTrail: auditTrail,
        }),
      });
      if (response.ok) {
        setLiveSwapAuditTrail(null);
        await fetchLeagueDay();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to apply swap');
      }
    } catch (err) {
      console.error('Error applying swap:', err);
      setError('Failed to apply swap');
    } finally {
      setApplyingSwap(false);
    }
  };

  const getPlayerName = (playerId: string) => {
    const player =
      allPlayers.find((p) => p.id === playerId) ||
      players.find((p) => p.id === playerId);
    return player?.name || "Unknown";
  };

  // Check if a player has been substituted and return the substitute's ID
  const getActualPlayerId = (playerId: string): string => {
    if (!leagueDay?.substitutions) return playerId;
    
    // Check if this player was substituted out
    const substitution = leagueDay.substitutions.find(
      (sub) => sub.originalPlayerId === playerId
    );
    
    if (substitution) {
      return substitution.substitutePlayerId;
    }
    
    return playerId;
  };

  const getPlayerNumber = (playerId: string) => {
    if (!leagueDay?.playerAssignments) return "?";
    const assignment = leagueDay.playerAssignments.find(
      (a) => a.playerId === playerId
    );
    return assignment?.assignedNumber || "?";
  };

  const getSubstitutionInfo = (playerId: string) => {
    if (!leagueDay?.substitutions) return null;
    // The backend physically replaces original player IDs in match documents,
    // so a slot's player ID is already the substitute. Only check "as substitute".
    return leagueDay.substitutions.find(
      (sub) => sub.substitutePlayerId === playerId
    ) || null;
  };

  // Fetch scorer names for matches
  const fetchScorerNames = async (matches: Match[]) => {
    const scorerIds = matches
      .filter((match) => match.scoredBy)
      .map((match) => match.scoredBy!)
      .filter((id, index, self) => self.indexOf(id) === index); // Unique IDs

    const names: { [key: string]: string } = {};

    for (const scorerId of scorerIds) {
      try {
        // Try fetching from scorekeepers first
        let response = await apiRequest(`/api/scorekeepers/${scorerId}`);
        if (response.ok) {
          const scorekeeper = await response.json();
          names[scorerId] = `${scorekeeper.username} (Scorekeeper)`;
          continue;
        }

        // If not found, try admins
        response = await apiRequest(`/api/admins/${scorerId}`);
        if (response.ok) {
          const admin = await response.json();
          names[scorerId] = `${admin.username} (Admin)`;
          continue;
        }

        // Fallback to ID if neither found
        names[scorerId] = scorerId;
      } catch (error) {
        console.error(`Error fetching scorer name for ${scorerId}:`, error);
        names[scorerId] = scorerId;
      }
    }

    setScorerNames(names);
  };

  const handleOpenScoreDialog = (match: Match) => {
    console.log("[Matches] Opening score dialog for match:", {
      id: match.id,
      roundNumber: match.roundNumber,
      courtNumber: match.courtNumber,
      score: match.score,
    });

    if (!match.id) {
      console.error("[Matches] Match has no ID!", match);
      setError("Invalid match data - missing ID");
      return;
    }

    setSelectedMatch(match);
    setPair1Score(match.score.pair1Games);
    setPair2Score(match.score.pair2Games);
    setForceComplete(false);
    setScoreDialogOpen(true);
  };

  const handleUpdateScore = async () => {
    if (!selectedMatch) {
      console.error("[Matches] No selected match");
      return;
    }

    if (!selectedMatch.id) {
      console.error("[Matches] Selected match has no ID:", selectedMatch);
      setError("Invalid match - missing ID");
      return;
    }

    try {
      console.log("[Matches] Updating score for match:", selectedMatch.id, {
        pair1Games: pair1Score,
        pair2Games: pair2Score,
        forceComplete,
      });

      const response = await apiRequest(
        `/api/matches/${selectedMatch.id}/score`,
        {
          method: "PUT",
          body: JSON.stringify({
            score: {
              pair1Games: pair1Score,
              pair2Games: pair2Score,
              isCompleted: false, // Will be determined by server
              winnerPair: null, // Will be determined by server
            },
            forceComplete,
          }),
        }
      );

      console.log("[Matches] Score update response status:", response.status);

      if (response.ok) {
        const updatedMatch = await response.json();
        console.log("[Matches] Score updated successfully:", updatedMatch);

        // Update the match in the league day state immediately
        if (leagueDay && leagueDay.rounds) {
          setLeagueDay((prev) => {
            if (!prev || !prev.rounds) return prev;
            return {
              ...prev,
              rounds: prev.rounds.map((round) => ({
                ...round,
                matches: round.matches.map((match: Match) =>
                  match.id === selectedMatch.id ? updatedMatch : match
                ),
              })),
            };
          });
        }

        setScoreDialogOpen(false);
        setError("");

        // Also refresh the full league day data as backup
        console.log("[Matches] Refreshing league day data...");
        await fetchLeagueDay();
        console.log(
          "[Matches] League day refreshed at:",
          new Date().toISOString()
        );
      } else {
        const errorData = await response.json();
        console.error("[Matches] Score update failed:", errorData);
        setError(errorData.error || "Failed to update score");
      }
    } catch (error) {
      console.error("Error updating score:", error);
      setError("Failed to update score");
    }
  };

  const handleOpenSubstitutionDialog = () => {
    setOriginalPlayerId("");
    setSubstitutePlayerId("");
    setSubstitutionError("");
    setApplyPenalty(false);
    setPenaltyPoints(3);
    setPenaltyReason("");
    // Initialize all rounds as selected by default
    const allRounds = leagueDay?.rounds?.map((_, index) => index + 1) || [
      1, 2, 3, 4,
    ];
    setSelectedRoundsForSubstitution(allRounds);
    setSubstitutionDialogOpen(true);
  };

  const handleSubstitutePlayer = async () => {
    if (!originalPlayerId || !substitutePlayerId) {
      setSubstitutionError("Please select both players");
      return;
    }

    if (selectedRoundsForSubstitution.length === 0) {
      setSubstitutionError("Please select at least one round");
      return;
    }

    if (applyPenalty && !penaltyReason.trim()) {
      setSubstitutionError("Please provide a reason for the penalty");
      return;
    }

    // Check if league day is completed - show warning for retroactive substitution
    if (leagueDay?.isCompleted) {
      setRetroactiveWarningOpen(true);
      return;
    }

    // If not completed, proceed with substitution
    await performSubstitution();
  };

  const performSubstitution = async () => {
    try {
      const requestBody: any = {
        originalPlayerId,
        substitutePlayerId,
        rounds: selectedRoundsForSubstitution,
      };

      // Add penalty data if checkbox is checked
      if (applyPenalty) {
        requestBody.applyPenalty = true;
        requestBody.penaltyPoints = penaltyPoints;
        requestBody.penaltyReason = penaltyReason.trim();
        requestBody.penaltyDivision = leagueDay?.division;
      }

      const response = await apiRequest(
        `/api/league-days/${leagueDayId}/substitute-player`,
        {
          method: "PUT",
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log("Substitution successful:", result);
        setSubstitutionDialogOpen(false);
        setRetroactiveWarningOpen(false);
        setSubstitutionError("");
        // Refresh league day to show updated data
        await fetchLeagueDay();
      } else {
        const errorData = await response.json();
        setSubstitutionError(errorData.error || "Failed to substitute player");
      }
    } catch (error) {
      console.error("Error substituting player:", error);
      setSubstitutionError("Failed to substitute player");
    }
  };

  const handleOpenEditPairsDialog = (match: Match) => {
    setEditingMatch(match);
    setEditPair1Player1(match.pair1.player1Id);
    setEditPair1Player2(match.pair1.player2Id);
    setEditPair2Player1(match.pair2.player1Id);
    setEditPair2Player2(match.pair2.player2Id);
    setEditPairsDialogOpen(true);
  };

  const handleSaveEditedPairs = async () => {
    if (!editingMatch || !editingMatch.id) {
      setError("Invalid match data");
      return;
    }

    // Validation: Check that all 4 players are different
    const playerIds = [
      editPair1Player1,
      editPair1Player2,
      editPair2Player1,
      editPair2Player2,
    ];
    const uniquePlayers = new Set(playerIds);
    if (uniquePlayers.size !== 4) {
      setError("All 4 players must be different");
      return;
    }

    // Validation: Check that all players are from the league day
    if (leagueDay && leagueDay.selectedPlayers) {
      const allValid = playerIds.every((id) =>
        leagueDay.selectedPlayers.includes(id)
      );
      if (!allValid) {
        setError("All players must be from this league day");
        return;
      }
    }

    try {
      const response = await apiRequest(
        `/api/matches/${editingMatch.id}/pairs`,
        {
          method: "PUT",
          body: JSON.stringify({
            pair1: {
              player1Id: editPair1Player1,
              player2Id: editPair1Player2,
            },
            pair2: {
              player1Id: editPair2Player1,
              player2Id: editPair2Player2,
            },
          }),
        }
      );

      if (response.ok) {
        setEditPairsDialogOpen(false);
        setError("");
        await fetchLeagueDay();
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to update match pairs");
      }
    } catch (error) {
      console.error("Error updating match pairs:", error);
      setError("Failed to update match pairs");
    }
  };

  const formatScore = (score: GameScore, maxGames: number, isTrial: boolean = false) => {
    if (score.pair1Games === 0 && score.pair2Games === 0) {
      return "Not started";
    }

    const scoreLabel = isTrial ? "pts" : "";
    if (score.isCompleted) {
      return `${score.pair1Games}-${score.pair2Games}${scoreLabel} (Final)`;
    } else {
      return `${score.pair1Games}-${score.pair2Games}${scoreLabel} (In progress)`;
    }
  };

  const getScoreColor = (score: GameScore, maxGames: number) => {
    if (score.pair1Games === 0 && score.pair2Games === 0) return "default";
    if (score.isCompleted) return "success";
    return "warning";
  };

  const renderMatchCard = (match: Match, courtIndex: number) => {
    const isCompleted = match.score.isCompleted;
    const winningScore = Math.ceil(match.maxGames / 2);

    return (
      <Card
        key={match.id}
        sx={{
          border: isCompleted ? "2px solid" : "1px solid",
          borderColor: isCompleted ? "success.main" : "divider",
          backgroundColor: isCompleted ? "action.hover" : "background.paper",
        }}
      >
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" color="primary">
              Court {match.courtNumber}
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <IconButton
                size="small"
                onClick={() => handleOpenEditPairsDialog(match)}
                color="primary"
                title="Edit player pairs"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <Chip
                label={
                  leagueDay?.isTrial 
                    ? "Point-Based" 
                    : match.maxGames === 9 
                    ? "Best of 9" 
                    : "Best of 7"
                }
                size="small"
                color={leagueDay?.isTrial ? "secondary" : "info"}
              />
            </Box>
          </Box>

          {/* Team 1 */}
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Team 1{" "}
              {match.score.winnerPair === 1 && (
                <TrophyIcon sx={{ fontSize: 16, color: "gold" }} />
              )}
            </Typography>
            <Box display="flex" gap={1} flexDirection="column">
              <Box display="flex" gap={1}>
                <Chip
                  label={`${getPlayerNumber(
                    match.pair1.player1Id
                  )}. ${getPlayerName(match.pair1.player1Id)}`}
                  variant="outlined"
                  size="small"
                />
                {getSubstitutionInfo(match.pair1.player1Id) && (
                  <Chip
                    label={`sub for ${
                      getSubstitutionInfo(match.pair1.player1Id)
                        ?.originalPlayerName
                    }`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
              <Box display="flex" gap={1}>
                <Chip
                  label={`${getPlayerNumber(
                    match.pair1.player2Id
                  )}. ${getPlayerName(match.pair1.player2Id)}`}
                  variant="outlined"
                  size="small"
                />
                {getSubstitutionInfo(match.pair1.player2Id) && (
                  <Chip
                    label={`sub for ${
                      getSubstitutionInfo(match.pair1.player2Id)
                        ?.originalPlayerName
                    }`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* VS */}
          <Box textAlign="center" mb={2}>
            <Typography variant="h6" color="text.secondary">
              VS
            </Typography>
          </Box>

          {/* Team 2 */}
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Team 2{" "}
              {match.score.winnerPair === 2 && (
                <TrophyIcon sx={{ fontSize: 16, color: "gold" }} />
              )}
            </Typography>
            <Box display="flex" gap={1} flexDirection="column">
              <Box display="flex" gap={1}>
                <Chip
                  label={`${getPlayerNumber(
                    match.pair2.player1Id
                  )}. ${getPlayerName(match.pair2.player1Id)}`}
                  variant="outlined"
                  size="small"
                />
                {getSubstitutionInfo(match.pair2.player1Id) && (
                  <Chip
                    label={`sub for ${
                      getSubstitutionInfo(match.pair2.player1Id)
                        ?.originalPlayerName
                    }`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
              <Box display="flex" gap={1}>
                <Chip
                  label={`${getPlayerNumber(
                    match.pair2.player2Id
                  )}. ${getPlayerName(match.pair2.player2Id)}`}
                  variant="outlined"
                  size="small"
                />
                {getSubstitutionInfo(match.pair2.player2Id) && (
                  <Chip
                    label={`sub for ${
                      getSubstitutionInfo(match.pair2.player2Id)
                        ?.originalPlayerName
                    }`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Score */}
          <Box textAlign="center" mb={2}>
            <Chip
              label={formatScore(match.score, match.maxGames, leagueDay?.isTrial || false)}
              color={getScoreColor(match.score, match.maxGames)}
              sx={{ fontSize: "1rem", height: "32px" }}
            />
          </Box>

          {/* Scored By Information */}
          {isCompleted && match.scoredBy && match.scoredAt && (
            <Box textAlign="center" mb={2}>
              <Typography variant="caption" color="text.secondary">
                Scored by: {scorerNames[match.scoredBy] || match.scoredBy}
                <br />
                on{" "}
                {new Date(
                  (match.scoredAt as any)._seconds
                    ? (match.scoredAt as any)._seconds * 1000
                    : match.scoredAt
                ).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            </Box>
          )}

          {/* Update Score Button */}
          <Button
            variant={isCompleted ? "outlined" : "contained"}
            startIcon={isCompleted ? <CheckIcon /> : <AddIcon />}
            onClick={() => handleOpenScoreDialog(match)}
            fullWidth
            color={isCompleted ? "success" : "primary"}
          >
            {isCompleted ? "View/Edit Score" : "Enter Score"}
          </Button>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <LinearProgress sx={{ width: "50%" }} />
      </Box>
    );
  }

  if (error || !leagueDay) {
    return (
      <Box>
        <Alert severity="error">{error || "League day not found"}</Alert>
        <Button onClick={() => navigate("/league-days")} sx={{ mt: 2 }}>
          Back to League Days
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4">
            League Day - {new Date(leagueDay.date).toLocaleDateString("en-GB")}
            {leagueDay.isTrial && (
              <Chip 
                label="TRIAL DAY" 
                color="secondary" 
                size="small" 
                sx={{ ml: 2, fontWeight: "bold" }}
              />
            )}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {leagueDay.rounds?.length} Round
            {leagueDay.rounds?.length !== 1 ? "s" : ""} 
            {leagueDay.isTrial && " (Point-Based Scoring)"} •{" "}
            {leagueDay.courtCount} Court{leagueDay.courtCount !== 1 ? "s" : ""}{" "}
            per Round
          </Typography>
          <VenueBadge
            venueName={leagueDay.venueName}
            venueAddress={leagueDay.venueAddress}
            variant="body2"
          />
          {leagueDay.isTrial && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
              ⚠️ Trial day matches do not affect player rankings or statistics
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="warning"
            onClick={handleOpenSubstitutionDialog}
          >
            Substitute Player
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate("/admin/league-days")}
          >
            Back to League Days
          </Button>
        </Box>
      </Box>

      {/* Division Display */}
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: "1px solid rgba(25, 118, 210, 0.1)",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            color: "#1976d2",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          🏆 Division: {leagueDay.division || "Division 1"}
        </Typography>
      </Paper>

      {/* Partnership Assignment Transparency */}
      {(() => {
        const savedTrail = leagueDay.swapAuditTrail;
        const activeTrail = savedTrail || liveSwapAuditTrail;
        const hasSwaps = activeTrail?.swapsPerformed?.length > 0 || activeTrail?.playerChanges?.length > 0;

        return (
          <Box sx={{ mb: 3 }}>
            {savedTrail ? (
              <SwapAuditTrailDisplay auditTrail={savedTrail} compact={false} />
            ) : (
              <>
                {!liveSwapAuditTrail && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAnalyseSwaps}
                    disabled={loadingSwapAnalysis}
                  >
                    {loadingSwapAnalysis ? 'Analysing…' : 'Analyse Repeat Partnerships'}
                  </Button>
                )}
                {loadingSwapAnalysis && <LinearProgress sx={{ mt: 1 }} />}
                {liveSwapAuditTrail && (
                  <SwapAuditTrailDisplay auditTrail={liveSwapAuditTrail} compact={false} />
                )}
              </>
            )}

            {activeTrail && hasSwaps && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => handleApplySwap(activeTrail)}
                  disabled={applyingSwap}
                >
                  {applyingSwap ? 'Applying…' : 'Apply Swap Corrections to Draw'}
                </Button>
              </Box>
            )}
          </Box>
        );
      })()}

      {/* Substitution History */}
      {leagueDay.substitutions && leagueDay.substitutions.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>Player Substitutions:</strong>
          </Typography>
          {leagueDay.substitutions.map((sub, index) => (
            <Typography key={index} variant="body2">
              • {sub.originalPlayerName} → {sub.substitutePlayerName}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Partners & Opponents per player */}
      {leagueDay.rounds && leagueDay.rounds.length > 0 && (
        <PlayerPairingsOverview
          rounds={leagueDay.rounds}
          playerNumbers={Object.fromEntries(
            (leagueDay.playerAssignments || []).map((a) => [
              a.playerId,
              a.assignedNumber,
            ]),
          )}
          getPlayerName={getPlayerName}
        />
      )}

      {/* Progress Overview */}
      <Card sx={{ mb: 3, overflow: "visible" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            League Day Progress
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
              flexWrap: "wrap",
              justifyContent: { xs: "space-between", sm: "flex-start" },
            }}
          >
            {leagueDay.rounds?.map((round) => {
              const isCompleted = round.isCompleted || false;
              const isCurrent =
                (leagueDay.currentRound || 1) === round.roundNumber;

              return (
                <Box
                  key={round.roundNumber}
                  textAlign="center"
                  sx={{ minWidth: "fit-content" }}
                >
                  <Chip
                    label={`Round ${round.roundNumber}`}
                    color={
                      isCompleted
                        ? "success"
                        : isCurrent
                        ? "primary"
                        : "default"
                    }
                    variant={isCurrent ? "filled" : "outlined"}
                    size="small"
                  />
                  {!leagueDay?.isTrial && (leagueDay.courtCount === 1 || round.roundNumber === 4) && (
                    <Typography
                      variant="caption"
                      display="block"
                      mt={0.5}
                      sx={{ fontSize: "0.7rem" }}
                    >
                      (Best of 9)
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* Round Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedRound}
          onChange={(_, newValue) => setSelectedRound(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTabs-scrollButtons": {
              "&.Mui-disabled": { opacity: 0.3 },
            },
            "& .MuiTabs-flexContainer": {
              gap: { xs: 0, sm: 1 },
            },
            "& .MuiTabs-scroller": {
              overflow: "auto !important",
            },
            minHeight: { xs: 48, sm: 48 },
          }}
        >
          {leagueDay.rounds?.map((round, index) => {
            const isCompleted = round.isCompleted || false;

            return (
              <Tab
                key={round.roundNumber}
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box
                      sx={{
                        display: { xs: "none", sm: "inline" },
                      }}
                    >
                      Round {round.roundNumber}
                    </Box>
                    <Box
                      sx={{
                        display: { xs: "inline", sm: "none" },
                      }}
                    >
                      R{round.roundNumber}
                    </Box>
                    {isCompleted && <CheckIcon sx={{ fontSize: 16 }} />}
                    {(leagueDay.courtCount === 1 ||
                      round.roundNumber === 4) && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: "0.75em",
                          display: { xs: "none", sm: "inline" },
                        }}
                      >
                        (Best of 9)
                      </Box>
                    )}
                  </Box>
                }
                sx={{
                  minWidth: { xs: 60, sm: 120 },
                  px: { xs: 1, sm: 2 },
                }}
              />
            );
          })}
        </Tabs>
      </Paper>

      {/* Round Content */}
      {leagueDay.rounds?.map((round, index) => {
        return (
          <TabPanel key={round.roundNumber} value={selectedRound} index={index}>
            <Typography variant="h5" gutterBottom>
              Round {round.roundNumber}
              {(leagueDay.courtCount === 1 || round.roundNumber === 4) && (
                <span style={{ fontSize: "0.8em", color: "#666" }}>
                  {" "}
                  (Best of 9
                  {round.roundNumber === 4 && leagueDay.courtCount !== 1
                    ? " - Final Round"
                    : ""}
                  )
                </span>
              )}
            </Typography>

            {round.matches ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(auto-fit, minmax(280px, 1fr))",
                    lg: "repeat(3, 1fr)",
                  },
                  gap: 3,
                  width: "100%",
                }}
              >
                {round.matches
                  .sort((a, b) => a.courtNumber - b.courtNumber)
                  .map((match, courtIndex) => (
                    <Box key={match.id} sx={{ width: "100%" }}>
                      {renderMatchCard(match, courtIndex)}
                    </Box>
                  ))}
              </Box>
            ) : (
              <Alert severity="info">No matches found for this round</Alert>
            )}
          </TabPanel>
        );
      })}

      {/* Score Entry Dialog */}
      <Dialog
        open={scoreDialogOpen}
        onClose={() => setScoreDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Enter Score - Court {selectedMatch?.courtNumber} (Round{" "}
          {selectedMatch?.roundNumber})
        </DialogTitle>
        <DialogContent>
          {selectedMatch && (
            <Box>
              {leagueDay?.isTrial ? (
                // Trial day - point-based scoring
                <>
                  <Typography variant="subtitle1" gutterBottom color="primary">
                    Trial Day - Point-Based Scoring
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Enter the final score after 15 minutes. Winner must have at least 1 point more.
                  </Typography>
                </>
              ) : (
                // Regular league day - game-based scoring
                <Typography variant="subtitle1" gutterBottom>
                  {selectedMatch.maxGames === 9
                    ? "Best of 9 Games"
                    : "Best of 7 Games"}
                  (First to {Math.ceil(selectedMatch.maxGames / 2)} wins)
                </Typography>
              )}

              <Box display="flex" alignItems="center" gap={2} mt={2}>
                <Box flex={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    Team 1 {leagueDay?.isTrial ? 'Points' : 'Score'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {getPlayerName(selectedMatch.pair1.player1Id)} &{" "}
                    {getPlayerName(selectedMatch.pair1.player2Id)}
                  </Typography>
                  <TextField
                    type="number"
                    value={pair1Score}
                    onChange={(e) =>
                      setPair1Score(Math.max(0, parseInt(e.target.value) || 0))
                    }
                    inputProps={{
                      min: 0,
                      max: leagueDay?.isTrial ? undefined : Math.ceil(selectedMatch.maxGames / 2),
                    }}
                    fullWidth
                  />
                </Box>

                <Typography variant="h4" color="text.secondary" sx={{ mt: 3 }}>
                  :
                </Typography>

                <Box flex={1}>
                  <Typography variant="subtitle2" gutterBottom>
                    Team 2 {leagueDay?.isTrial ? 'Points' : 'Score'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {getPlayerName(selectedMatch.pair2.player1Id)} &{" "}
                    {getPlayerName(selectedMatch.pair2.player2Id)}
                  </Typography>
                  <TextField
                    type="number"
                    value={pair2Score}
                    onChange={(e) =>
                      setPair2Score(Math.max(0, parseInt(e.target.value) || 0))
                    }
                    inputProps={{
                      min: 0,
                      max: leagueDay?.isTrial ? undefined : Math.ceil(selectedMatch.maxGames / 2),
                    }}
                    fullWidth
                  />
                </Box>
              </Box>

              {!leagueDay?.isTrial && (
                <Box sx={{ mt: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={forceComplete}
                        onChange={(e) => setForceComplete(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Mark as Complete (use when time expires)"
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ ml: 4 }}
                  >
                    Check this to accept any score as final, overriding the
                    best-of requirement
                  </Typography>
                </Box>
              )}

              {leagueDay?.isTrial && pair1Score === pair2Score && (pair1Score > 0 || pair2Score > 0) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Trial matches cannot end in a tie. One team must win by at least 1 point.
                </Alert>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScoreDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleUpdateScore} 
            variant="contained"
            disabled={leagueDay?.isTrial && pair1Score === pair2Score && (pair1Score > 0 || pair2Score > 0)}
          >
            Update Score
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Match Pairs Dialog */}
      <Dialog
        open={editPairsDialogOpen}
        onClose={() => setEditPairsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Match Pairs</DialogTitle>
        <DialogContent>
          {editingMatch && (
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                Editing Court {editingMatch.courtNumber} - Round{" "}
                {editingMatch.roundNumber}
              </Alert>

              {/* Team 1 */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Team 1
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Player 1</InputLabel>
                    <Select
                      value={editPair1Player1}
                      label="Player 1"
                      onChange={(e) => setEditPair1Player1(e.target.value)}
                    >
                      {leagueDay?.selectedPlayers.map((playerId) => {
                        const player = players.find((p) => p.id === playerId);
                        return (
                          <MenuItem key={playerId} value={playerId}>
                            {getPlayerNumber(playerId)}.{" "}
                            {player?.name || "Unknown"}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Player 2</InputLabel>
                    <Select
                      value={editPair1Player2}
                      label="Player 2"
                      onChange={(e) => setEditPair1Player2(e.target.value)}
                    >
                      {leagueDay?.selectedPlayers.map((playerId) => {
                        const player = players.find((p) => p.id === playerId);
                        return (
                          <MenuItem key={playerId} value={playerId}>
                            {getPlayerNumber(playerId)}.{" "}
                            {player?.name || "Unknown"}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Team 2 */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Team 2
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Player 1</InputLabel>
                    <Select
                      value={editPair2Player1}
                      label="Player 1"
                      onChange={(e) => setEditPair2Player1(e.target.value)}
                    >
                      {leagueDay?.selectedPlayers.map((playerId) => {
                        const player = players.find((p) => p.id === playerId);
                        return (
                          <MenuItem key={playerId} value={playerId}>
                            {getPlayerNumber(playerId)}.{" "}
                            {player?.name || "Unknown"}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel>Player 2</InputLabel>
                    <Select
                      value={editPair2Player2}
                      label="Player 2"
                      onChange={(e) => setEditPair2Player2(e.target.value)}
                    >
                      {leagueDay?.selectedPlayers.map((playerId) => {
                        const player = players.find((p) => p.id === playerId);
                        return (
                          <MenuItem key={playerId} value={playerId}>
                            {getPlayerNumber(playerId)}.{" "}
                            {player?.name || "Unknown"}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPairsDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEditedPairs}
            variant="contained"
            color="primary"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Player Substitution Dialog */}
      <Dialog
        open={substitutionDialogOpen}
        onClose={() => setSubstitutionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Substitute Player</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Select a player who is not showing up and choose their replacement.
            The substitute will play all of the original player's matches and
            earn all points.
          </Typography>

          {substitutionError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {substitutionError}
            </Alert>
          )}

          {/* Original Player Selection */}
          <FormControl fullWidth sx={{ mt: 3 }}>
            <InputLabel>Player Who is Absent</InputLabel>
            <Select
              value={originalPlayerId}
              label="Player Who is Absent"
              onChange={(e) => setOriginalPlayerId(e.target.value)}
            >
              <MenuItem value="">
                <em>Select player</em>
              </MenuItem>
              {leagueDay?.selectedPlayers
                .filter((id) => {
                  if (leagueDay?.waitlistPlayerIds?.includes(id)) return false;
                  if (leagueDay?.playerAssignments?.length) {
                    return leagueDay.playerAssignments.some(
                      (a) => a.playerId === id
                    );
                  }
                  return true;
                })
                .map((playerId) => {
                const player = allPlayers.find((p) => p.id === playerId);
                const playerNumber = getPlayerNumber(playerId);
                return (
                  <MenuItem key={playerId} value={playerId}>
                    #{playerNumber} - {player?.name || "Unknown"} (
                    {player?.division})
                  </MenuItem>
                );
              })}
              {/* Players already subbed in, so a wrong substitute can be replaced */}
              {Array.from(
                new Map(
                  (leagueDay?.substitutions || []).map((s) => [
                    s.substitutePlayerId,
                    s,
                  ])
                ).values()
              )
                .filter(
                  (s) => !leagueDay?.selectedPlayers.includes(s.substitutePlayerId)
                )
                .map((s) => (
                  <MenuItem key={s.substitutePlayerId} value={s.substitutePlayerId}>
                    {s.substitutePlayerName} (subbed in for {s.originalPlayerName}
                    {s.rounds?.length
                      ? `, round${s.rounds.length > 1 ? "s" : ""} ${s.rounds.join(", ")}`
                      : ""}
                    )
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Substitute Player Selection */}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Substitute Player</InputLabel>
            <Select
              value={substitutePlayerId}
              label="Substitute Player"
              onChange={(e) => setSubstitutePlayerId(e.target.value)}
              disabled={!originalPlayerId}
            >
              <MenuItem value="">
                <em>Select substitute</em>
              </MenuItem>
              {allPlayers
                .filter(
                  (p) =>
                    p.isActive &&
                    (!leagueDay?.selectedPlayers.includes(p.id) ||
                      leagueDay?.waitlistPlayerIds?.includes(p.id) ||
                      (!!leagueDay?.playerAssignments?.length &&
                        !leagueDay.playerAssignments.some(
                          (a) => a.playerId === p.id
                        )))
                )
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((player) => (
                  <MenuItem key={player.id} value={player.id}>
                    {player.name} ({player.division})
                    {player.division !== leagueDay?.division &&
                      " - Cross-Division"}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Round Selection */}
          {originalPlayerId && (
            <Box sx={{ mt: 3 }}>
              <FormLabel component="legend">
                Select Rounds for Substitution
              </FormLabel>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1 }}
              >
                Choose which rounds the substitute should play in
              </Typography>
              <FormGroup row>
                {leagueDay?.rounds?.map((round, index) => {
                  const roundNumber = index + 1;
                  return (
                    <FormControlLabel
                      key={roundNumber}
                      control={
                        <Checkbox
                          checked={selectedRoundsForSubstitution.includes(
                            roundNumber
                          )}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRoundsForSubstitution([
                                ...selectedRoundsForSubstitution,
                                roundNumber,
                              ]);
                            } else {
                              setSelectedRoundsForSubstitution(
                                selectedRoundsForSubstitution.filter(
                                  (r) => r !== roundNumber
                                )
                              );
                            }
                          }}
                        />
                      }
                      label={`Round ${roundNumber}`}
                    />
                  );
                })}
              </FormGroup>
            </Box>
          )}

          {/* Penalty Section */}
          {originalPlayerId && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: "rgba(211, 47, 47, 0.05)", borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={applyPenalty}
                    onChange={(e) => setApplyPenalty(e.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Apply penalty to {getPlayerName(originalPlayerId)}
                  </Typography>
                }
              />
              
              {applyPenalty && (
                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Penalty Points</InputLabel>
                    <Select
                      value={penaltyPoints}
                      label="Penalty Points"
                      onChange={(e) => setPenaltyPoints(e.target.value as number)}
                    >
                      <MenuItem value={3}>3 points</MenuItem>
                      <MenuItem value={6}>6 points</MenuItem>
                      <MenuItem value={9}>9 points</MenuItem>
                      <MenuItem value={12}>12 points</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label="Penalty Reason"
                    value={penaltyReason}
                    onChange={(e) => setPenaltyReason(e.target.value)}
                    multiline
                    rows={2}
                    placeholder="E.g., No-show without prior notice"
                    required
                  />
                </Box>
              )}
            </Box>
          )}

          {originalPlayerId &&
            substitutePlayerId &&
            selectedRoundsForSubstitution.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This will replace{" "}
                <strong>{getPlayerName(originalPlayerId)}</strong> with{" "}
                <strong>{getPlayerName(substitutePlayerId)}</strong> in{" "}
                <strong>
                  {selectedRoundsForSubstitution.length ===
                  (leagueDay?.rounds?.length || 4)
                    ? "all rounds"
                    : `round${
                        selectedRoundsForSubstitution.length > 1 ? "s" : ""
                      } ${selectedRoundsForSubstitution.join(", ")}`}
                </strong>
                . All points earned will go to{" "}
                {getPlayerName(substitutePlayerId)}.
              </Alert>
            )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubstitutionDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubstitutePlayer}
            variant="contained"
            color="primary"
            disabled={!originalPlayerId || !substitutePlayerId}
          >
            Confirm Substitution
          </Button>
        </DialogActions>
      </Dialog>

      {/* Retroactive Substitution Warning Dialog */}
      <Dialog
        open={retroactiveWarningOpen}
        onClose={() => setRetroactiveWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span style={{ fontSize: "1.5rem" }}>⚠️</span>
          Retroactive Substitution Warning
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This league day has already been completed!
          </Alert>
          
          <Typography variant="body1" gutterBottom>
            You are about to perform a <strong>retroactive substitution</strong> on a completed league day. This will:
          </Typography>
          
          <Box component="ul" sx={{ pl: 2, mt: 2, mb: 2 }}>
            <li>
              <Typography variant="body2" gutterBottom>
                Reverse all statistics for this league day
              </Typography>
            </li>
            <li>
              <Typography variant="body2" gutterBottom>
                Update match records with the correct player
              </Typography>
            </li>
            <li>
              <Typography variant="body2" gutterBottom>
                Recalculate all player statistics and rankings
              </Typography>
            </li>
          </Box>

          <Typography variant="body2" color="text.secondary">
            <strong>Original player:</strong> {getPlayerName(originalPlayerId)}
            <br />
            <strong>Substitute player:</strong> {getPlayerName(substitutePlayerId)}
            <br />
            <strong>Rounds affected:</strong> {selectedRoundsForSubstitution.join(", ")}
          </Typography>

          <Alert severity="info" sx={{ mt: 2 }}>
            This action is safe and will ensure the statistics are accurate. All changes will be logged.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setRetroactiveWarningOpen(false);
              setSubstitutionDialogOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={performSubstitution}
            variant="contained"
            color="warning"
          >
            Proceed with Retroactive Substitution
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Matches;
